// O2 (`mapped.jsonl`) and O4 (`TriageSummary`) — the two artifacts that sit
// between the validate step and the write path.
//
// Source: `docs/initiatives/new/dashboard-import-tool/import-engine-contract.md`
// §3 (O2 at "### O2", O4 at "### O4") and §7's conformance bullets.
//
// O2 is called out in the annex as "the highest-value seam in this document":
// it is the ENTIRE input to the Inngest workflow (SPEC §3.3). Get it wrong and
// Milestone B either blocks or guesses.
//
// ---------------------------------------------------------------------------
// O2 FILE FORMAT — JSON Lines. One JSON object per line, UTF-8, LF-terminated.
// **Not a single JSON array**: a 50k-row array cannot be streamed and forces the
// whole dataset into Lambda memory at once.
//
// > SPEC amendment (forced by the annex): SPEC §3.7 previously named this file
// > `mapped.json`. It is `mapped.jsonl`. Keeping a `.json` extension on a JSONL
// > file is a small lie that costs an implementer an hour.
//
// ---------------------------------------------------------------------------
// O2's SIX BINDING OBLIGATIONS. Three are encoded below as schema refinements
// or predicates; the other three are properties of how the file is PRODUCED and
// CONSUMED, and can only be stated:
//
//  1. **Excluded rows ARE present in the file.** They are not dropped at
//     validate time. The write path needs them to emit per-row reasons into
//     `results.csv` and to make the SPEC §3.1 counts invariant
//     (`total = created + merged + excluded + errored`) reconcile. A file
//     containing only READY rows makes the completion screen unable to account
//     for every row, which breaks OVERVIEW Principle 1.
//  3. **Values are already transformed.** Phones are E.164, emails are
//     lowercased and trimmed, dates are ISO, consent is canonical. The workflow
//     applies **zero** business transforms. The identity rails remain the
//     *authority* on these invariants (INV-1) — this contract guarantees their
//     input already conforms, so rails-side normalization is an idempotent
//     no-op, not a second opinion that could disagree.
//  6. **Never read by anything but the write path.** The Hub and the wizard read
//     O1/O3/O4. A second reader of this file is a coupling bug.
//
// (2, 4 and 5 are `rowLineCountMatches`, the READY/needs_review refinement, and
// `mappedDatasetKey` respectively — see each below.)

import { z } from 'zod';

import {
  columnTargetSchema,
  resolvedConsentSchema,
  sourceFileHintSchema,
  targetFieldKeySchema,
  type TargetFieldKey,
} from './fields.js';

// ===========================================================================
// PART 1 — the code vocabularies
// ===========================================================================
//
// ⚠️ **DERIVED, not transcribed.** The annex references `ExclusionCode`,
// `RowWarning`, `ValidationCode` and `TransformRule` by name but publishes no
// membership list for any of them. SPEC §5.4.1 and §5.4.2 DO define the actual
// rules, as tables. So every member below is derived one-per-SPEC-rule, and
// each carries an inline comment naming the rule it came from.
//
// Per annex §8, adding a code is an **ADDITIVE** change (cheap, encouraged) and
// consumers MUST tolerate unknown codes: render them, count them, never crash
// on them. That tolerance is the whole reason additive evolution stays cheap —
// a consumer that switch-throws on an unknown code turns a new warning into an
// outage.

/**
 * Row-level exclusions — the row does not reach the writer at all.
 *
 * Derived from SPEC §5.4.1's admissibility table (both rows) plus the one
 * §5.4.2 field rule whose action is "Exclude row".
 */
export const EXCLUSION_CODES = [
  // ⚠️ RETIRED 2026-08-24 — NO LONGER EMITTED BY THE ENGINE.
  // Was: §5.4.1 "No usable identifier: no valid email AND no parseable phone".
  // That rule was removed entirely — see
  // `docs/initiatives/new/dashboard-import-tool/decisions.md#D-NAME-ONLY-CONTACTS-ARE-IMPORTABLE`.
  //
  // KEPT IN THE UNION ON PURPOSE, not left behind by accident. Annex §8 blesses
  // ADDITIVE change and requires consumers to tolerate codes they do not know,
  // but says nothing about REMOVAL — and the asymmetry bites here: an O2
  // artifact written minutes before the deploy can still carry this code, and a
  // narrowed union would fail to PARSE it, taking out the review screen for a
  // file that validated fine. Reserving costs one dead union member; removing
  // costs a §8 seam escalation and a backward-compat break. Do not "tidy" these
  // two away without re-reading §8.
  'NO_USABLE_IDENTIFIER',
  // §5.4.1 — "No usable person name". As of 2026-08-25 a row needs BOTH a
  // non-blank `firstName` AND a non-blank `lastName`. A single name column
  // qualifies only when it SPLITS into both ("Ada Lovelace" does; "Cher" does
  // not, because the split leaves lastName empty). `organizationName` is
  // metadata and does NOT lift the requirement, so an ORGANIZATION-ONLY row now
  // carries this code where it previously imported — a deliberate, ratified
  // reversal, see
  // `docs/initiatives/new/dashboard-import-tool/decisions.md#D-PERSON-NAME-REQUIRED`.
  //
  // The code is REUSED rather than joined by a new sibling: the reason a row is
  // rejected ("we cannot name this contact") is unchanged, only the threshold
  // moved. A new member would have been an annex §8 seam change for no gain in
  // what a consumer can tell the user.
  //
  // We still do NOT synthesize a name from the email local-part: a fabricated
  // name is indistinguishable from a real one once stored, and the org could
  // never tell which of their contacts are real.
  'NO_USABLE_NAME',
  // ⚠️ RETIRED 2026-08-24 — NO LONGER EMITTED. Reserved for the same
  // parse-compatibility reason as `NO_USABLE_IDENTIFIER` above.
  //
  // Was: §5.4.2 Email — "Fails is_valid_email ... Exclude row if it was the
  // only identifier", which front-ran the §5.4.3 writer bug (the Python writer
  // SILENTLY DROPS invalid emails and continues, producing a contact with zero
  // emails and zero lookup rows — unfindable, unmergeable, un-emailable).
  //
  // That writer branch is now REACHABLE and accepted: such a row imports as a
  // name-only contact and the user is told via the `EMAIL_INVALID_DROPPED`
  // warning instead of having the row silently removed.
  'INVALID_EMAIL_ONLY_IDENTIFIER',
  // §5.4.2 Email (Auto-fix "Collapse to one row") — the ABSORBED side of an
  // in-file duplicate collapse. ADDITIVE (annex §8), added by PE-1898.
  //
  // It exists because the absorbed row must stay in the file (O2 obligation 1:
  // excluded rows are never dropped) but is NOT an error: it has a perfectly
  // valid identifier, and it was combined into its twin rather than rejected.
  // Coding it `NO_USABLE_IDENTIFIER` — the only prior fit — rendered "add an
  // email or phone and re-upload" at ERROR severity for a row that already had
  // one, and double-counted the error bucket against `duplicatesCollapsed`.
  // The remediation for this code is "no action needed".
  'COLLAPSED_INTO_DUPLICATE',
  // §9.3 / O2 obligation 4 — consent resolved to `needs_review` and no human
  // has confirmed it yet. ADDITIVE (annex §8), added by PE-1898.
  //
  // Obligation 4 forbids `needs_review` on a READY row and permits it on an
  // EXCLUDED one, so "unresolved consent" HAS to be expressible as an
  // exclusion or the artifact simply cannot be written (the row would fail to
  // parse either way). This code is what makes the honest third state —
  // "held back pending your confirmation", distinct from both "imported" and
  // "rejected as unusable" — representable in O2 at all.
  //
  // It is NOT a permanent rejection: it clears when the user confirms the
  // consent value in the validate step and the file is re-validated. Its
  // triage severity is deliberately not ERROR — nothing is wrong with the row.
  'CONSENT_NEEDS_REVIEW',
] as const;

export const exclusionCodeSchema = z.enum(EXCLUSION_CODES);
export type ExclusionCode = z.infer<typeof exclusionCodeSchema>;

/**
 * Per-row warnings and auto-fixes — the row still imports, but something was
 * changed or degraded and the user must be able to see it.
 *
 * Derived one-per-rule from SPEC §5.4.2's Warning and Auto-fix rows.
 */
export const ROW_WARNING_CODES = [
  // §5.4.2 Phone — unparseable, not a possible number, or national number under
  // MIN_PHONE_DIGITS. Import without the phone.
  'PHONE_UNPARSEABLE',
  // §5.4.2 Phone — bare national format (no `+`, no `00`), normalized using the
  // file-level default region (§5.3). Surfaced in the triage preview showing the
  // resulting E.164, NEVER applied silently: the writer's hardcoded US default
  // means a bare 5551234567 SUCCEEDS into the wrong country, so no "unparseable"
  // rule can catch it.
  'PHONE_BARE_NATIONAL_NORMALIZED',
  // §5.4.2 Name — single `name` column requiring a split. Shown in the
  // auto-fixed bucket WITH before/after, because a wrong split is silent
  // corruption and nobody reviews 8,000 name splits.
  'NAME_SPLIT_APPLIED',
  // §5.4.2 Address — all six fields blank; drop the address entirely (an
  // all-blank address still lands on contact.addresses[] if passed through).
  'ADDRESS_ALL_BLANK_DROPPED',
  // §5.4.2 Tag — value over MAX_TAG_LENGTH (50). Rejected, NOT truncated: the
  // tagger rejects rather than trimming. Row still imports.
  'TAG_TOO_LONG_REJECTED',
  // §5.4.2 Tag — value not in the org's contact_tags dictionary; pre-created at
  // commit time (§5.4.3).
  'TAG_PRECREATED',
  // §5.4.2 Tag — duplicate within a row, case-insensitively. The dictionary's
  // casing wins.
  'TAG_DUPLICATE_COLLAPSED',
  // §5.4.2 Consent — unrecognized value; requires manual confirmation in Step 3.
  'CONSENT_VALUE_UNRECOGNIZED',
  // §5.4.2 Consent — a recognized SMS OPT-IN value. Never auto-granted; mapped
  // to needs_review per §9.1. Import can only ever carry SMS opt-OUTS.
  'CONSENT_SMS_OPT_IN_DOWNGRADED',
  // §5.4.2 Custom field — value over the §7.1 255-char cap; truncated and
  // flagged, never silent.
  'CUSTOM_FIELD_TRUNCATED',
  // §5.4.2 Date — unparseable; import without the date.
  'DATE_UNPARSEABLE',
  // §5.4.2 Email — multiple emails on one row, none marked primary. First
  // non-empty becomes primary: the writer guarantees exactly one, so we choose
  // deterministically rather than letting CSV column order decide.
  'EMAIL_PRIMARY_CHOSEN',
  // §5.4.2 Email — duplicate WITHIN the file, compared post-normalization
  // (strip().lower()). Collapsed to one row; see `collapsedFrom`.
  'EMAIL_IN_FILE_DUPLICATE_COLLAPSED',
  // §5.4.2 Email ("else drop the email and flag") — the email failed
  // is_valid_email but the row survived on another identifier, so the address
  // is dropped. ADDITIVE (annex §8), added by PE-1898.
  //
  // The rule's own words are "drop the email AND FLAG"; without a code there
  // was no flag, and a bad address vanished with no trace on any row a phone
  // rescued. That is the same silent-drop class §5.4.3 documents about the
  // Python writer — the failure this whole module exists to front-run.
  'EMAIL_INVALID_DROPPED',
  // §3.4 rule 4 — a merge found BOTH sides non-blank and DIFFERENT on a
  // single-valued scalar field (`firstName`/`lastName`/`organizationName`/a
  // `custom_fields.<key>`). Rule 4 declines the write correctly (fill-only,
  // never overwrite), but until this code existed the decline was invisible:
  // the results file's `reason`/`warnings` were empty and `merged: 1` reads
  // identically whether the merge was clean or every field was declined
  // (PE-1584 F-17). `detail` carries the retained/incoming pair so a
  // support agent (or the donor) can see exactly what did not get applied
  // without re-deriving it from the two files.
  'MERGE_FIELD_CONFLICT',
] as const;

export const rowWarningCodeSchema = z.enum(ROW_WARNING_CODES);
export type RowWarningCode = z.infer<typeof rowWarningCodeSchema>;

/**
 * The §5.4.2 "Info" rows — neither a failure nor a fix, but worth surfacing in
 * triage so the user is not surprised at commit time.
 */
export const INFO_CODES = [
  // §5.4.2 Email — matches an existing contact in the org. Will merge; preview
  // shown in Step 3. Feeds O4's `willMerge`.
  'EMAIL_MATCHES_EXISTING_CONTACT',
  // §5.4.2 Address — `country` present. Stored AS SUPPLIED, deliberately not
  // normalized to ISO-2: the writer's address dedup is a 6-field exact
  // signature over free text, so normalizing would make imported addresses fail
  // to dedupe against the donation path's existing verbatim values (§5.4.3).
  'ADDRESS_COUNTRY_STORED_AS_SUPPLIED',
] as const;

export const infoCodeSchema = z.enum(INFO_CODES);
export type InfoCode = z.infer<typeof infoCodeSchema>;

/**
 * File-LEVEL issues — facts about the upload as a whole, not about any single
 * row, so they may never appear as a row's `exclusionReason` or `warnings[]`
 * code. They exist only in O4's issue list. ADDITIVE (annex §8).
 */
export const FILE_ISSUE_CODES = [
  // §4.0 — the user declared an expected contact count at upload and the file
  // parsed materially short of it. "This is the only defence that works when
  // the damage happened outside our system" (a truncating export, an
  // assistant reshaping the file). The issue's `count` is the SHORTFALL
  // (expected − parsed), and `sampleRowNumbers` is empty — the missing rows,
  // by definition, have no row numbers.
  'ROW_COUNT_SHORTFALL',
] as const;

export const fileIssueCodeSchema = z.enum(FILE_ISSUE_CODES);
export type FileIssueCode = z.infer<typeof fileIssueCodeSchema>;

/**
 * O4's `TriageIssue.code` vocabulary: the union of everything that can be
 * counted and shown in the validate step's issue list.
 *
 * It is exactly `EXCLUSION_CODES ∪ ROW_WARNING_CODES ∪ INFO_CODES ∪
 * FILE_ISSUE_CODES` — one flat enum rather than four, because O4 renders a
 * single issue list and a consumer grouping by `severity` needs one key space
 * to look codes up in. The source arrays remain separately exported because
 * the per-ROW shapes are narrower: a `mappedRowLineSchema.exclusionReason` may
 * only be an `ExclusionCode`, and a `warnings[].code` only a `RowWarningCode`.
 * Using the wide union in those positions would let a row claim it was
 * excluded for an informational reason — or carry a file-level fact.
 */
export const VALIDATION_CODES = [
  ...EXCLUSION_CODES,
  ...ROW_WARNING_CODES,
  ...INFO_CODES,
  ...FILE_ISSUE_CODES,
] as const;

export const validationCodeSchema = z.enum(VALIDATION_CODES);
export type ValidationCode = z.infer<typeof validationCodeSchema>;

/**
 * `TransformRule` — the per-column deterministic transform recorded in O3 and
 * hinted at by P2.
 *
 * ⚠️ **The annex references `TransformRule` in two places (O3's `transform?`,
 * P2's `transformRuleHint?`) but never defines it.** This is derived from SPEC
 * §5.3's five deterministic transforms, and it is the **softest shape in this
 * file** — the one most likely to need an additive extension once Milestone C
 * implements the transforms for real. Treat a needed addition here as ordinary
 * additive evolution (§8), not as a seam break.
 */
export const transformRuleSchema = z.discriminatedUnion('kind', [
  z.object({
    /**
     * §5.3 phone normalization → E.164 via libphonenumber.
     *
     * Carries NO per-column config: the default region is a **file-level**
     * setting (§5.3, persisted on `MappingConfig.defaultPhoneRegion`), not a
     * per-column one. A per-column region would let two phone columns in one
     * file disagree about what country a bare number belongs to.
     */
    kind: z.literal('PHONE_E164'),
  }),
  z.object({
    /** §5.3 email normalization: trim, then lowercase — in that order. */
    kind: z.literal('EMAIL_NORMALIZE'),
  }),
  z.object({
    /**
     * §5.3 name splitting: trim, collapse internal whitespace, split on the
     * LAST space. Right for the common case, wrong for compound surnames and
     * suffixes — hence the mandatory auto-fixed triage bucket with before/after.
     */
    kind: z.literal('NAME_SPLIT'),
  }),
  z.object({
    /**
     * §5.3 date parsing. `detectedFormat` is required because DD/MM vs MM/DD is
     * a named silent-corruption risk: the format is detected once from a sample
     * and then applied CONSISTENTLY, so it must be recorded rather than
     * re-guessed per row.
     */
    kind: z.literal('DATE_PARSE'),
    detectedFormat: z.string(),
  }),
  z.object({
    /**
     * §5.3 consent enum mapping — platform value → our vocabulary (e.g.
     * Mailchimp `subscribed` → `opted_in`; `cleaned` → needs review, because it
     * means BOUNCED, not unsubscribed).
     */
    kind: z.literal('CONSENT_ENUM_MAP'),
    valueMap: z.record(z.string(), z.string()),
  }),
]);
export type TransformRule = z.infer<typeof transformRuleSchema>;

/** O3's `ColumnMapping.origin` — see the denormalized column below. */
export const COLUMN_MAPPING_ORIGINS = [
  'FINGERPRINT',
  'AI_ACCEPTED',
  'AI_OVERRIDDEN',
  'MANUAL',
] as const;
export const columnMappingOriginSchema = z.enum(COLUMN_MAPPING_ORIGINS);
export type ColumnMappingOrigin = z.infer<typeof columnMappingOriginSchema>;

// ===========================================================================
// PART 2 — O2, `mapped.jsonl`
// ===========================================================================

/**
 * O3's `ColumnMapping`, **denormalized into the O2 header line** so the file is
 * self-describing for support and for the results writer.
 *
 * Defined inline rather than imported: this is a COPY that travels with the
 * file, and it is deliberately decoupled from whatever the live O3 schema
 * becomes. If O3's shape changes, this must change with it AND the header
 * line's `version` must bump (annex §8) — an old `mapped.jsonl` sitting in S3
 * still has to be readable by the code that finds it.
 */
export const denormalizedColumnMappingSchema = z.object({
  /** Verbatim from the file, never normalized. */
  header: z.string(),
  /** 0-based position in the file. */
  columnIndex: z.number().int().nonnegative(),
  target: columnTargetSchema,
  /** Required iff `target === 'CUSTOM_FIELD'`. */
  customFieldKey: z.string().optional(),
  transform: transformRuleSchema.optional(),
  /**
   * Required on every column. This is what makes the funnel's
   * mapping-override rate (SPEC §3.8) computable from the durable record rather
   * than from UI-only telemetry.
   */
  origin: columnMappingOriginSchema,
});
export type DenormalizedColumnMapping = z.infer<typeof denormalizedColumnMappingSchema>;

/**
 * Line 0 of `mapped.jsonl` — exactly one, always first.
 */
export const mappedHeaderLineSchema = z.object({
  kind: z.literal('HEADER'),
  /**
   * Schema version of this file. `1` in V1 — "V1 is version 1 everywhere"
   * (annex §8). It exists so a future migration has something to switch on;
   * `z.literal(1)` means a v2 file fails loudly against a v1 reader instead of
   * being half-understood.
   */
  version: z.literal(1),
  /** Asserted against the invoking job, not trusted. */
  jobId: z.string(),
  orgId: z.string(),
  /**
   * Stable hash of the O3 the rows were produced under. The workflow asserts it
   * matches the job's CURRENT `mappingConfig` and fails loudly on mismatch —
   * this is the guard against a stale S3 object after a
   * READY → MAPPING → re-validate cycle.
   */
  mappingConfigHash: z.string(),
  /** Count of `ROW` lines that follow. See `rowLineCountMatches`. */
  rowCount: z.number().int().nonnegative(),
  columns: z.array(denormalizedColumnMappingSchema),
  /**
   * Carried so the write path can attribute consent basis without re-reading
   * the job record (annex §4.1b).
   */
  sourceFileHint: sourceFileHintSchema,
  generatedAt: z.string().datetime(),
  /** Identifies the validate-step build that wrote the file. */
  generatorVersion: z.string(),
});
export type MappedHeaderLine = z.infer<typeof mappedHeaderLineSchema>;

export const rowWarningSchema = z.object({
  code: rowWarningCodeSchema,
  /**
   * Deliberately minimal — the annex says only "each with a code". `detail` and
   * `field` exist because the results file has to render something a human can
   * act on ("row 412: phone +1555… was normalized from 5551234567"), but
   * nothing downstream may PARSE either: the code is the machine-readable part.
   */
  detail: z.string().optional(),
  field: z.string().optional(),
});
export type RowWarning = z.infer<typeof rowWarningSchema>;

const mappedRowLineBaseSchema = z.object({
  kind: z.literal('ROW'),
  /**
   * **1-based index into the ORIGINAL file, excluding the header row.** Never
   * re-indexed, never compacted — not even when rows are collapsed or excluded.
   *
   * This is what every user-facing message and every results-file line cites
   * ("row 412 of your file"). Re-indexing after a collapse would make every
   * number the user sees point at the wrong row of the file on their disk.
   */
  sourceRowNumber: z.number().int().positive(),
  /** Whether the write path should attempt a write. */
  outcome: z.enum(['READY', 'EXCLUDED']),
  /**
   * Standard-field values keyed by `TargetFieldKey`, **post-transform and
   * write-ready**. An absent key means no value for that field.
   *
   * zod 3 has no `partialRecord`, so this is `z.record(enum, value)` made
   * partial via `.partial()` on the inferred type — expressed here with an
   * explicit `Partial<>` annotation rather than a runtime `.partial()`, which
   * `z.record` does not support.
   */
  contact: z.record(targetFieldKeySchema, z.string()) as z.ZodType<
    Partial<Record<TargetFieldKey, string>>
  >,
  /** Blank cells produce NO key (SPEC §7.2). */
  customFields: z.record(z.string(), z.string()),
  /** Tag values mapped from `TAG` columns. */
  tags: z.array(z.string()),
  /** Already resolved — the write path never re-derives it (annex §4.1b). */
  consent: resolvedConsentSchema,
  /** **Required when `outcome: 'EXCLUDED'`**, absent otherwise. */
  exclusionReason: exclusionCodeSchema.optional(),
  /** Auto-fixes applied and non-fatal problems. Feeds the results file. */
  warnings: z.array(rowWarningSchema),
  /**
   * Source row numbers of in-file duplicates collapsed into this row (§5.4).
   *
   * The annex says "Present only when non-empty", so producers OMIT the key
   * rather than emitting `[]` — an empty array and an absent key are not the
   * same statement, and a reader counting `collapsedFrom` keys to report
   * "N rows were merged" would over-count if empties were written.
   */
  collapsedFrom: z.array(z.number().int().positive()).optional(),
});

/**
 * Lines 1..N of `mapped.jsonl`.
 *
 * Two of O2's obligations are enforced here as **parse errors**, which is a
 * deliberate choice — see the contrast note on `triageSummaryReconciles()`.
 * Both describe data that must never reach the writer at all, and both would
 * otherwise fail silently and unrecoverably:
 *
 *   * **Obligation (excluded rows carry a reason).** A reasonless exclusion is
 *     a row the user can never be told about. It vanishes from their file with
 *     no explanation available in `results.csv`, which is exactly the
 *     per-row-accountability failure OVERVIEW Principle 1 exists to prevent.
 *   * **Obligation 4 (no `needs_review` consent on a READY row).** Those must be
 *     resolved before commit (SPEC §9.3). A `needs_review` value reaching the
 *     write path means we are about to write a consent state a human never
 *     confirmed — the one class of bug in this system that is not recoverable
 *     by a reversal, because a wrongly-granted permission may already have been
 *     acted on.
 */
export const mappedRowLineSchema = mappedRowLineBaseSchema.superRefine((row, ctx) => {
  if (row.outcome === 'EXCLUDED' && row.exclusionReason === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['exclusionReason'],
      message: 'exclusionReason is required when outcome is EXCLUDED',
    });
  }
  if (row.outcome === 'READY' && row.exclusionReason !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['exclusionReason'],
      message: 'exclusionReason must be absent when outcome is READY',
    });
  }
  if (row.outcome === 'READY' && row.consent.email === 'needs_review') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['consent', 'email'],
      message:
        'a READY row may not carry needs_review consent — it must be resolved ' +
        'or the row excluded before commit (SPEC §9.3)',
    });
  }
});
export type MappedRowLine = z.infer<typeof mappedRowLineSchema>;

/**
 * Any line of `mapped.jsonl`.
 *
 * NOTE: this is `z.union`, not `z.discriminatedUnion`, because
 * `mappedRowLineSchema` carries a `.superRefine()` and zod 3's
 * `discriminatedUnion` requires each option to be a bare `ZodObject`. The
 * behavioral difference is only error-message quality on a malformed line.
 */
export const mappedLineSchema = z.union([mappedHeaderLineSchema, mappedRowLineSchema]);
export type MappedLine = z.infer<typeof mappedLineSchema>;

// ---------------------------------------------------------------------------
// O2 obligations as callable checks
// ---------------------------------------------------------------------------

/**
 * O2 obligation 5 — **immutable once written.**
 *
 * A re-validate (`READY → MAPPING → VALIDATING → READY`, permitted by SPEC
 * §3.2) writes a **new key** and repoints the job record's `mappedFileKey`.
 * Overwriting in place races a commit already in flight: the workflow could
 * read a half-rewritten file, and the `mappingConfigHash` check would not
 * necessarily catch it because the new file's hash is also valid.
 *
 * Key layout from SPEC §3.7: `{orgId}/{jobId}/mapped.jsonl`, then
 * `mapped-2.jsonl`, `mapped-3.jsonl`. `revision` is 1-based; revision 1 is the
 * unsuffixed name.
 */
export function mappedDatasetKey(orgId: string, jobId: string, revision = 1): string {
  if (!Number.isInteger(revision) || revision < 1) {
    throw new Error(`mappedDatasetKey: revision must be a positive integer, got ${revision}`);
  }
  const filename = revision === 1 ? 'mapped.jsonl' : `mapped-${revision}.jsonl`;
  return `${orgId}/${jobId}/${filename}`;
}

/**
 * O2 obligation 2 — **`rowCount` is the number of `ROW` lines, and the job
 * record's `total` is that same number.** One number, one meaning, asserted at
 * read.
 *
 * A short or long file is a **hard failure, not a partial import**: if the file
 * has fewer rows than its header claims, importing what's there would silently
 * drop contacts the user believes they imported, and the §3.1 counts invariant
 * would never reconcile.
 */
export function rowLineCountMatches(header: MappedHeaderLine, rowLineCount: number): boolean {
  return header.rowCount === rowLineCount;
}

/**
 * O2 — `exclusionReason` present iff the row is EXCLUDED.
 *
 * Also enforced as a parse error in `mappedRowLineSchema`. This predicate
 * exists for the conformance kit and for callers validating an
 * already-constructed object without re-parsing.
 */
export function excludedRowHasReason(row: MappedRowLine): boolean {
  return row.outcome === 'EXCLUDED'
    ? row.exclusionReason !== undefined
    : row.exclusionReason === undefined;
}

/**
 * O2 obligation 4 — no `needs_review` consent on a READY row.
 *
 * Also enforced as a parse error in `mappedRowLineSchema`; same rationale as
 * `excludedRowHasReason`.
 */
export function readyRowHasNoNeedsReviewConsent(row: MappedRowLine): boolean {
  if (row.outcome !== 'READY') return true;
  return row.consent.email !== 'needs_review';
}

// ===========================================================================
// PART 3 — O6, the source table (`source-{nonce}.jsonl`)  ·  ADM-35
// ===========================================================================
//
// WHAT THIS IS AND WHY IT EXISTS. `imports.validate` used to receive every data
// row in its POST body. The shared `ApiRouter`'s Lambda@Edge signer caps a
// request body at an exact 1 MiB (see `limits.ts`), which refuses an ordinary
// nonprofit contacts export at roughly 4,000-6,000 rows. O6 moves the rows out
// of the body: the browser PUTs them to S3 and the body carries only metadata.
//
// THE BYTES ARE THE BROWSER'S *DERIVED* TABLE, NOT THE UPLOADED FILE, and that
// distinction is the whole reason this artifact exists rather than the server
// simply reading `source.csv`. The wizard transforms the file on the way
// through: encoding repair (windows-1252 -> UTF-8), a name-column split that
// ADDS columns the file never had, trailing-blank strip and blank-row drops
// that change the row count, and in-grid cell edits that exist NOWHERE else.
// `confirmedColumns` is then joined by `columnIndex` POSITIONALLY against those
// derived headers. Re-parsing the original upload would produce a different
// table and silently write every value into the wrong field.
//
// DELIBERATELY DUMB — A TRANSPORT FORMAT, NOT A RE-EXPRESSION OF O2. It carries
// the header row, the cells, and enough identity to prove the object belongs to
// this job. Every judgment — the deterministic transforms, the validation
// rules, in-file dedupe and consent resolution — stays server-side, which is
// what keeps annex §4.3a's "four outcomes we own" ours. The rows are treated
// exactly as `input.rows` was: an UNTRUSTED `ParsedTable`, re-validated on
// read. If a field here ever encodes a DECISION rather than DATA, the seam has
// moved and that needs annex §8's process, not a patch.
//
// EVERY OBJECT IS WRITE-ONCE, KEYED BY A SERVER-MINTED NONCE. The hazard is the
// one documented for O2 obligation 5 above: a validate that is still running
// while the user edits a cell and re-clicks would otherwise have its object
// overwritten underneath it, and a hash check cannot catch that because the new
// file's hash is also valid. A fresh nonce per upload makes the overwrite
// unrepresentable rather than merely unlikely.
//
// RELATIONSHIP TO O1-O5: additive. O1-O5 keep their exact shapes and
// obligations, and nothing downstream reads O6 — it is consumed only by
// `validate`, inside the black box, on its way to producing O2. Registered in
// the annex for completeness rather than as a seam change.

/**
 * Schema version of a `source-{nonce}.jsonl` object.
 *
 * `z.literal` below rather than `z.number()`, for the same reason O2 pins its
 * version: a future v2 object read by a v1 reader must be REFUSED, not
 * half-understood.
 */
export const SOURCE_TABLE_VERSION = 1;

/**
 * Line 0 of the source table — exactly one, always first.
 *
 * `.strict()` is deliberate. Zod strips unknown keys by default, and a silently
 * dropped field is precisely the failure mode ADM-35's rollout has to avoid:
 * the value vanishes instead of the read failing, and the mismatch surfaces
 * later as wrong data rather than immediately as a refusal.
 */
export const sourceTableHeaderLineSchema = z
  .object({
    kind: z.literal('HEADER'),
    version: z.literal(SOURCE_TABLE_VERSION),
    /** Asserted against the invoking job, never trusted. */
    jobId: z.string().min(1),
    orgId: z.string().min(1),
    /**
     * The DERIVED headers, in derived order — the exact list `confirmedColumns`
     * was confirmed against. The procedure asserts this equals the `headers` in
     * the request body and refuses on mismatch, because a disagreement between
     * the two means the positional join would put cells in the wrong fields.
     */
    headers: z.array(z.string()).min(1),
    /** Count of `ROW` lines that follow. See `sourceTableRowCountMatches`. */
    rowCount: z.number().int().nonnegative(),
    /** Identifies the wizard build that serialized the object. */
    generatorVersion: z.string(),
  })
  .strict();
export type SourceTableHeaderLine = z.infer<typeof sourceTableHeaderLineSchema>;

/**
 * A data row of the source table.
 *
 * `null` is a real, meaningful value: P1 obligation 1 says the parser PADS a
 * short row with `null` rather than shifting cells left, so `null` is how "no
 * value in this column" is expressed without breaking the positional join.
 *
 * Width is checked against `headers.length` by the parser, not here — this
 * schema's job is the shape of one line.
 */
export const sourceTableRowLineSchema = z
  .object({
    kind: z.literal('ROW'),
    cells: z.array(z.string().nullable()),
  })
  .strict();
export type SourceTableRowLine = z.infer<typeof sourceTableRowLineSchema>;

/** Any line of a `source-{nonce}.jsonl` object. */
export const sourceTableLineSchema = z.discriminatedUnion('kind', [
  sourceTableHeaderLineSchema,
  sourceTableRowLineSchema,
]);
export type SourceTableLine = z.infer<typeof sourceTableLineSchema>;

/**
 * Key layout for a source table: `{orgId}/{jobId}/source-{nonce}.jsonl`.
 *
 * The nonce is minted SERVER-SIDE and never accepted from the caller — the same
 * rule `sourceFileKey` follows, and for the same reason (SPEC §3.7a explains
 * why a client-supplied key is refused). This function additionally refuses a
 * nonce that could escape the prefix: one containing a path separator or a dot
 * segment would aim the object at another job's prefix, which is exactly what
 * the read-side prefix guard exists to prevent. Refusing at construction as
 * well means a bug upstream cannot mint a traversing key in the first place.
 */
export function sourceTableKey(orgId: string, jobId: string, nonce: string): string {
  if (orgId === '') throw new Error('sourceTableKey: orgId must not be empty');
  if (jobId === '') throw new Error('sourceTableKey: jobId must not be empty');
  if (!/^[A-Za-z0-9_-]+$/.test(nonce)) {
    throw new Error(
      `sourceTableKey: nonce must be one or more of [A-Za-z0-9_-], got ${JSON.stringify(nonce)}`,
    );
  }
  return `${orgId}/${jobId}/source-${nonce}.jsonl`;
}

/**
 * Declared-vs-actual row count, mirroring O2's `rowLineCountMatches` and
 * existing for the same reason.
 *
 * A PUT that truncates mid-flight produces a file that is syntactically
 * perfect and merely SHORT. Nothing else would notice: every line parses, the
 * positional join works, and the import would quietly drop the contacts that
 * never arrived. A short or long file is therefore a hard failure, not a
 * partial import.
 */
export function sourceTableRowCountMatches(
  header: SourceTableHeaderLine,
  rowLineCount: number,
): boolean {
  return header.rowCount === rowLineCount;
}

// ===========================================================================
// PART 4 — O4, `TriageSummary`
// ===========================================================================

export const triageIssueSchema = z.object({
  code: validationCodeSchema,
  severity: z.enum(['ERROR', 'WARNING', 'INFO']),
  /**
   * How many times the code applies.
   *
   * ⚠️ FOR ROW-LEVEL CODES this is a number of rows IN THIS FILE, and is
   * therefore ≤ `total`. FOR `FILE_ISSUE_CODES` IT IS NOT — a
   * `ROW_COUNT_SHORTFALL` counts rows the file is MISSING, so it can exceed
   * `total` (declare 100, upload 2 ⇒ `count: 98`, `total: 2`). A renderer that
   * phrases this as "N of `total` rows" will produce "98 of 2" on that code.
   * Nothing sums `issue.count` today and reconciliation does not read
   * `issues[]`, so this is a display contract, not a counts invariant.
   */
  count: z.number().int().nonnegative(),
  /** At most 5, for rendering "e.g. rows 12, 47, 103". */
  sampleRowNumbers: z.array(z.number().int().positive()).max(5),
  /** Human-readable. **Display-only — never parsed.** */
  remediation: z.string(),
});
export type TriageIssue = z.infer<typeof triageIssueSchema>;

/**
 * What the validate step returns to the wizard (annex O4).
 *
 * Note the two subset relationships, which are NOT expressible in the schema
 * but are load-bearing for the triage UI:
 *   * `autoFixed` is a **subset of `ready`** — rows with ≥1 auto-fix warning.
 *   * `needsAttention` is a **subset of `ready`** requiring a human decision
 *     before commit. **`needsAttention > 0` BLOCKS COMMIT** until resolved or
 *     explicitly skipped.
 */
export const triageSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  ready: z.number().int().nonnegative(),
  excluded: z.number().int().nonnegative(),
  autoFixed: z.number().int().nonnegative(),
  needsAttention: z.number().int().nonnegative(),
  duplicatesCollapsed: z.number().int().nonnegative(),
  /** Of `ready`: no existing contact matches. */
  willCreate: z.number().int().nonnegative(),
  /** Of `ready`: email matches an existing contact. */
  willMerge: z.number().int().nonnegative(),
  consent: z.object({
    optedIn: z.number().int().nonnegative(),
    optedOut: z.number().int().nonnegative(),
    needsReview: z.number().int().nonnegative(),
    noData: z.number().int().nonnegative(),
  }),
  issues: z.array(triageIssueSchema),
  /** S3 key of the downloadable fix-externally CSV. */
  needsAttentionFileKey: z.string().optional(),
});
export type TriageSummary = z.infer<typeof triageSummarySchema>;

/**
 * O4's obligations: `total === ready + excluded` AND
 * `ready === willCreate + willMerge`.
 *
 * ⚠️ **DESIGN DECISION — this is a PREDICATE, deliberately NOT a
 * `.superRefine()` on `triageSummarySchema`.** Do not "fix" it into one.
 *
 * The annex's rule is that a mismatch renders as a **visible error state**,
 * never a silently wrong number — the same rule the Hub applies to its counts
 * row (SPEC §6.1). A refinement would make a mismatched summary fail to PARSE,
 * so the validate step would THROW and the user would see a crash instead of
 * the honest "these numbers don't add up" state the annex mandates. The caller
 * runs this check precisely so it can RENDER the failure.
 *
 * **Contrast with `mappedRowLineSchema`**, where the O2 obligations ARE parse
 * errors. The distinction is who the bad data reaches: an O2 violation is a
 * producer-side bug on data heading for the WRITER, where a bad row becomes a
 * wrong contact or an unexplainable exclusion — it must never get that far. An
 * O4 violation is a bad number heading for a SCREEN, where the honest move is
 * to show that it's wrong. Same contract, two different failure surfaces,
 * two different mechanisms.
 */
export function triageSummaryReconciles(summary: TriageSummary): boolean {
  return (
    summary.total === summary.ready + summary.excluded &&
    summary.ready === summary.willCreate + summary.willMerge
  );
}

/**
 * O4 — whether the commit step must be blocked pending human resolution.
 *
 * Separate from `triageSummaryReconciles` because these are different
 * questions: one asks "are these numbers self-consistent", the other "may the
 * user proceed". A summary can reconcile perfectly and still block.
 */
export function triageBlocksCommit(summary: TriageSummary): boolean {
  return summary.needsAttention > 0;
}

/**
 * ADDED BY PE-1904 (funnel instrumentation), additively — a new function, no
 * existing shape or meaning changed.
 *
 * Read the job record's `triage` field as a `TriageSummary`, or `null` if it is
 * absent or does not conform.
 *
 * WHY THIS EXISTS RATHER THAN A TIGHTER `triage` FIELD. SPEC §3.8 and
 * `decisions.md#D-FUNNEL-TRANSPORT` put the triage-category rates on the job
 * record so a dropped analytics event cannot cost them, and `import-job.ts`
 * duly persists them — but as `Record<string, unknown>`, "typed permissively on
 * purpose" so Milestone B never has to guess a vocabulary Milestone C owns.
 * That permissiveness is correct for the WRITE side and useless on the READ
 * side: a metrics consumer holding `Record<string, unknown>` cannot get at
 * `autoFixed` without an unchecked cast, and an unchecked cast is how a renamed
 * count silently becomes `undefined` in a dashboard.
 *
 * Narrowing the field itself would be the wrong fix twice over: it would change
 * an existing field's meaning (not additive), and it would make a job whose
 * validate step emitted an unknown `ValidationCode` fail to PARSE — turning the
 * annex §8 tolerate-unknown-codes rule into a crash on the read path.
 *
 * So the field stays permissive and the narrowing happens here, at the point of
 * use, where a non-conforming value degrades to `null` — a missing dashboard
 * row — instead of throwing.
 *
 * `null` is therefore genuinely ambiguous between "never validated" and
 * "validated, but the stored summary doesn't conform", and callers must not
 * substitute zeros for it: a zeroed triage summary reads as "0 rows needed
 * attention", which is a claim, whereas absence is the absence of a claim.
 */
export function readTriageSummary(
  triage: Record<string, unknown> | undefined,
): TriageSummary | null {
  if (!triage) return null;
  const parsed = triageSummarySchema.safeParse(triage);
  return parsed.success ? parsed.data : null;
}
